import React, {
  SelectHTMLAttributes,
  forwardRef,
  useState,
  useEffect,
  useRef,
} from 'react'
import { cn } from '../../utils/cn'

interface SelectOption {
  value: string
  label: string
}

interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string
  options: SelectOption[]
  placeholder?: string
  error?: string
  onValueChange?: (value: string) => void
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      label,
      options,
      placeholder = 'Selecciona',
      error,
      disabled,
      onValueChange,
      value,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
      null
    )
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Sincronizar el value prop con selectedOption
    useEffect(() => {
      if (value) {
        const option = options.find(opt => opt.value === value)
        if (option) {
          setSelectedOption(option)
        }
      } else {
        setSelectedOption(null)
      }
    }, [value, options])

    // Cerrar dropdown al hacer click fuera
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false)
        }
      }

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [isOpen])

    const handleOptionClick = (option: SelectOption) => {
      setSelectedOption(option)
      setIsOpen(false)

      // Call onValueChange if provided
      if (onValueChange) {
        onValueChange(option.value)
      }

      // Trigger change event for form libraries
      if (props.onChange) {
        const event = {
          target: { value: option.value },
        } as React.ChangeEvent<HTMLSelectElement>
        props.onChange(event)
      }
    }

    const baseClasses = cn(
      'w-full px-4 py-3 rounded-lg border transition-all duration-200 ease-in-out',
      'text-base font-normal cursor-pointer',
      'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
      'flex items-center justify-between',
      !error && !disabled && 'border-gray-300 hover:border-gray-400',
      error && 'border-red-500 focus:ring-red-500',
      disabled && 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
    )

    const labelClasses = cn(
      'block text-sm font-medium mb-2',
      !error && !disabled && 'text-gray-700',
      error && 'text-red-600',
      disabled && 'text-gray-500'
    )

    const dropdownClasses = cn(
      'absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto',
      isOpen ? 'block' : 'hidden'
    )

    const optionClasses =
      'px-4 py-3 text-base cursor-pointer hover:bg-gray-50 transition-colors'

    return (
      <div className="w-full relative" ref={dropdownRef}>
        {label && <label className={labelClasses}>{label}</label>}
        <div className="relative">
          <div
            className={cn(baseClasses, className)}
            onClick={() => !disabled && setIsOpen(!isOpen)}
          >
            <span
              className={selectedOption ? 'text-gray-900' : 'text-gray-400'}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <svg
              className={cn(
                'w-5 h-5 transition-transform duration-200',
                isOpen && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          <div className={dropdownClasses}>
            {options.map(option => (
              <div
                key={option.value}
                className={cn(
                  optionClasses,
                  selectedOption?.value === option.value &&
                    'bg-blue-50 text-blue-600'
                )}
                onClick={() => handleOptionClick(option)}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>

        {/* Hidden select for form compatibility */}
        <select
          ref={ref}
          className="hidden"
          value={selectedOption?.value || ''}
          disabled={disabled}
          {...props}
        >
          <option value="">{placeholder}</option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      </div>
    )
  }
)

Select.displayName = 'Select'
